import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, where, doc, deleteDoc, orderBy, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Container, 
  Alert,
  Button, // Added Button
  ListGroup // Added ListGroup for booked holidays view
} from 'react-bootstrap';
import SimpleDatePicker from './SimpleDatePicker'; // Import our custom date picker
import './HolidayPlanner.css'; // Import the custom CSS
import NotificationModal from './NotificationModal';
import './NotificationModal.css'; // Import styles for NotificationModal
import ConfirmationModal from './ConfirmationModal';
import './ConfirmationModal.css'; // Import styles for ConfirmationModal

/**
 * HolidayPlanner component
 * Helps users plan holidays by showing which days they need to take off
 */
const HolidayPlanner = ({
  currentUser, 
  show, 
  onClose, // Changed from handleClose
  // Props from MobileMenuBar for Stage 1 shift selection
  initialCompanies,      // Array of company names
  initialPatternsData,   // Object: { companyName: [patternName1, patternName2] }
  allShiftsData,         // Array of all shift objects for all company/patterns
  propSelectedCompany,
  propSelectedPattern,
  propSelectedShift,
  handleCompanyChangeProp,
  handlePatternChangeProp,
  handleShiftChangeProp,
  onHolidayBooked // Callback to refresh calendar holidays
}) => {
  // Format a date to YYYY-MM-DD string
  const formatDateForInput = useCallback((dateInput) => {
    let dateToFormat = dateInput;
    if (!(dateInput instanceof Date) || isNaN(dateInput.getTime())) {
      dateToFormat = new Date(dateInput || Date.now());
      if (isNaN(dateToFormat.getTime())) return ''; // Still invalid after attempt
    }
    const year = dateToFormat.getFullYear();
    const month = String(dateToFormat.getMonth() + 1).padStart(2, '0');
    const day = String(dateToFormat.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Parse date from input format (YYYY-MM-DD) to Date object
  const parseInputDate = useCallback((dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return new Date(NaN);
    const parts = dateStr.split('-');
    if (parts.length !== 3) return new Date(NaN);

    const year = parseInt(parts[0], 10);
    const monthNum = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const dayNum = parseInt(parts[2], 10);

    if (isNaN(year) || isNaN(monthNum) || isNaN(dayNum)) {
        return new Date(NaN);
    }
    if (monthNum < 0 || monthNum > 11 || dayNum < 1 || dayNum > 31 || year < 1000 || year > 3000) {
        return new Date(NaN);
    }
    return new Date(year, monthNum, dayNum);
  }, []);

  // Notification Modal State and Helper
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState({ title: '', message: '', variant: 'info' });

  // Confirmation Modal State
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({ title: '', message: '', onConfirm: () => {} });

  const triggerNotification = (title, message, variant = 'info') => {
    setNotificationConfig({ title, message, variant });
    setShowNotificationModal(true);
  };
  
  // State variables
  // plannerStage: 0 = initial choice, 1 = pick start date, 2 = pick end date, 3 = summary/confirm, 4 = view booked holidays
  const [plannerStage, setPlannerStage] = useState(0);
  const [startDateStr, setStartDateStr] = useState(formatDateForInput(new Date()));
  const [endDateStr, setEndDateStr] = useState(formatDateForInput(new Date(new Date().setDate(new Date().getDate() + 7))));
  const [calendarDays, setCalendarDays] = useState([]);

  const [summary, setSummary] = useState({
    totalDays: 0,
    workDays: 0,
    daysOff: 0,
    holidaysNeeded: 0
  });

  // Reset to stage 0 and default dates when modal is shown
  useEffect(() => {
    if (show) {
      setPlannerStage(0);
      setStartDateStr(formatDateForInput(new Date()));
      setEndDateStr(formatDateForInput(new Date(new Date().setDate(new Date().getDate() + 7))));
    }
  }, [show, formatDateForInput]); // formatDateForInput is stable due to useCallback

  // useEffect to calculate holiday summary when dates or shift change, or when entering stage 3
  useEffect(() => {
    if (plannerStage === 3 && startDateStr && endDateStr && allShiftsData) {
      const start = parseInputDate(startDateStr);
      const end = parseInputDate(endDateStr);

      if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
        setSummary({ totalDays: 0, workDays: 0, daysOff: 0, holidaysNeeded: 0 });
        setCalendarDays([]);
        return;
      }

      let currentShiftPattern = null;
      let patternStartDate = null;
      let selectedShiftDetails = null;

      if (propSelectedShift && allShiftsData.length > 0) {
        selectedShiftDetails = allShiftsData.find(s => s.id === propSelectedShift || s.name === propSelectedShift);

        if (selectedShiftDetails && selectedShiftDetails.pattern) {
          currentShiftPattern = selectedShiftDetails.pattern;
          if (selectedShiftDetails.patternStartDate) {
            patternStartDate = parseInputDate(selectedShiftDetails.patternStartDate);
            if (isNaN(patternStartDate.getTime())) {
              console.warn(`Invalid patternStartDate for shift ${propSelectedShift}: ${selectedShiftDetails.patternStartDate}. Defaulting.`);
              patternStartDate = new Date(start.getFullYear(), 0, 1);
            }
          } else {
            console.warn(`patternStartDate not found for shift ${propSelectedShift}. Defaulting. This may lead to incorrect shift calculations.`);
            patternStartDate = new Date(start.getFullYear(), 0, 1);
          }
        }
      }

      let totalDays = 0;
      let workDaysCount = 0;
      let daysOffCount = 0;
      const newCalendarDays = [];
      const dayNamesForCalendar = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; // For day name display

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        totalDays++;
        const currentDate = new Date(d);
        const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
        let isWorkDay = false;

        if (currentShiftPattern && patternStartDate && !isNaN(patternStartDate.getTime())) {
          // Use UTC to avoid DST issues and ensure accurate day counting
          const utcCurrent = Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
          const utcStart = Date.UTC(patternStartDate.getFullYear(), patternStartDate.getMonth(), patternStartDate.getDate());
          const diffTime = utcCurrent - utcStart;
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          const patternIndex = (diffDays % currentShiftPattern.length + currentShiftPattern.length) % currentShiftPattern.length; // Ensure positive index
          const shiftCode = currentShiftPattern[patternIndex];
          if (shiftCode && shiftCode.toUpperCase() !== 'O') { // Assuming 'O' or 'o' means Off-day
            isWorkDay = true;
          }
        } else {
          // Fallback: Standard Mon-Fri work week if no specific pattern or pattern details are incomplete
          if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Monday to Friday
            isWorkDay = true;
          }
        }

        if (isWorkDay) {
          workDaysCount++;
        } else {
          daysOffCount++;
        }

        newCalendarDays.push({
          dateStr: formatDateForInput(currentDate),
          dayOfMonth: currentDate.getDate(),
          dayName: dayNamesForCalendar[dayOfWeek],
          isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
          isWorkDay: isWorkDay,
        });
      }

      setSummary({
        totalDays: totalDays,
        workDays: workDaysCount,
        daysOff: daysOffCount,
        holidaysNeeded: workDaysCount 
      });
      setCalendarDays(newCalendarDays);
    } else if (plannerStage !== 3) {
      // Optionally reset summary if not in stage 3 to avoid showing stale data
      setSummary({ totalDays: 0, workDays: 0, daysOff: 0, holidaysNeeded: 0 });
      setCalendarDays([]);
    }
  }, [plannerStage, startDateStr, endDateStr, propSelectedShift, allShiftsData, parseInputDate, formatDateForInput]);

  const [userBookedHolidays, setUserBookedHolidays] = useState([]);
  const [loadingBookedHolidays, setLoadingBookedHolidays] = useState(false);
  const [errorBookedHolidays, setErrorBookedHolidays] = useState('');

  // Fetch user's booked holidays
  const fetchUserBookedHolidays = useCallback(async () => {
    if (!currentUser) return;
    setLoadingBookedHolidays(true);
    setErrorBookedHolidays('');
    try {
      const holidaysRef = collection(db, 'holidays');
      const q = query(
        holidaysRef,
        where('userId', '==', currentUser.uid),
        orderBy('startDate', 'asc') // Order by start date
      );
      const querySnapshot = await getDocs(q);
      const holidays = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        console.log('Raw holiday data from Firestore for doc ID ' + docSnap.id + ':', JSON.parse(JSON.stringify(data))); // Log raw data, stringify for Timestamps

        let startDate, endDate;
        try {
          // Attempt to convert Firestore Timestamps (or other date representations) to JS Date objects
          startDate = data.startDate?.toDate ? data.startDate.toDate() : new Date(data.startDate);
          endDate = data.endDate?.toDate ? data.endDate.toDate() : new Date(data.endDate);

          // Validate the converted dates
          if (isNaN(startDate.getTime())) {
            console.error('Invalid startDate after conversion for holiday ID:', docSnap.id, '; Original data.startDate:', data.startDate);
            // Fallback or decide how to handle invalid dates, e.g., skip this holiday or use a placeholder
            // For now, it will proceed as an Invalid Date object, which might cause issues in rendering
          }
          if (isNaN(endDate.getTime())) {
            console.error('Invalid endDate after conversion for holiday ID:', docSnap.id, '; Original data.endDate:', data.endDate);
            // Fallback or decide how to handle invalid dates
          }
        } catch (dateConversionError) {
          console.error('Error during date conversion for holiday ID:', docSnap.id, dateConversionError);
          // Fallback if conversion itself throws an error (e.g., data.startDate is null/undefined and new Date(null) is problematic)
          startDate = new Date(0); // Use epoch or another placeholder for invalid/unconvertible date
          endDate = new Date(0);   // Use epoch or another placeholder
        }

        holidays.push({
          id: docSnap.id,
          ...data, // Keep original data as well, if needed elsewhere
          startDate: startDate, // Use the (potentially validated/fallback) Date object
          endDate: endDate,   // Use the (potentially validated/fallback) Date object
        });
      });
      setUserBookedHolidays(holidays);
    } catch (error) {
      console.error('Error fetching booked holidays:', error);
      setErrorBookedHolidays('Failed to load your booked holidays. Please try again.');
    }
    setLoadingBookedHolidays(false);
  }, [currentUser]);

  // Effect to fetch booked holidays when stage 2 is active
  useEffect(() => {
    if (plannerStage === 4 && currentUser) {
      fetchUserBookedHolidays();
    }
  }, [plannerStage, currentUser, fetchUserBookedHolidays]);

  // Handle initiating holiday cancellation (shows confirmation modal)
  const handleCancelHoliday = (holidayId) => {
    setConfirmationConfig({
      title: 'Confirm Cancellation',
      message: 'Are you sure you want to cancel this holiday?',
      onConfirm: () => executeHolidayCancellation(holidayId) // Pass the ID directly
    });
    setShowConfirmationModal(true);
  };

  // Execute the actual holiday cancellation after confirmation
  const executeHolidayCancellation = async (idToCancel) => {
    setShowConfirmationModal(false); // Close confirmation modal first
    if (!idToCancel) return;

    try {
      const holidayDocRef = doc(db, 'holidays', idToCancel);
      await deleteDoc(holidayDocRef);
      fetchUserBookedHolidays();
      if (onHolidayBooked) {
        onHolidayBooked(); // Call the callback to refresh holidays in Calendar.js
      }
      triggerNotification('Holiday Cancelled', 'Holiday cancelled successfully!', 'success');
    } catch (error) {
      console.error('Error cancelling holiday:', error);
      triggerNotification('Cancellation Error', `Failed to cancel holiday: ${error.message}`, 'danger');
    }
  };

  // Handle confirming and booking the holiday
  const handleConfirmHoliday = async () => {
    if (!currentUser) {
      triggerNotification('Login Required', 'You must be logged in to confirm a holiday.', 'warning');
      return;
    }
    if (!startDateStr || !endDateStr) {
      triggerNotification('Date Selection Required', 'Please select both a start and end date for your holiday.', 'warning');
      return;
    }

    try {
      const holidaysCollectionRef = collection(db, 'holidays');
      await addDoc(holidaysCollectionRef, {
        userId: currentUser.uid,
        startDate: parseInputDate(startDateStr), // Ensure dates are stored as Date objects or Timestamps
        endDate: parseInputDate(endDateStr),
        status: 'confirmed',
        bookedAt: new Date()
      });
      triggerNotification('Success', 'Holiday successfully booked!', 'success');
      if (onHolidayBooked) {
        onHolidayBooked(); // Call the callback to refresh holidays in Calendar.js
      }
      fetchUserBookedHolidays(); // Refresh booked holidays list within HolidayPlanner
      if (onClose) onClose(); // Close the modal on success, using the correct prop name
    } catch (error) {
      console.error('Failed to book holiday:', error);
      triggerNotification('Booking Error', `Failed to book holiday: ${error.message}`, 'danger');
    }
  };

  // Month names for display
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Day names for the calendar header
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // (Booking flow does not use company/pattern/shift logic anymore)
  // If you need shift pattern logic for the calendar in the future, restore here.

  // Calculate and update holiday details, including summary and calendar view
  const updateHolidayDetails = useCallback(() => {
    if (!startDateStr || !endDateStr) {
      setCalendarDays([]);
      setSummary({ totalDays: 0, workDays: 0, daysOff: 0, holidaysNeeded: 0 });
      return;
    }

    const start = parseInputDate(startDateStr);
    const end = parseInputDate(endDateStr);

    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
      setCalendarDays([]);
      setSummary({ totalDays: 0, workDays: 0, daysOff: 0, holidaysNeeded: 0 });
      return;
    }

    let currentShiftPattern = null;
    let patternStartDate = null;
    if (propSelectedShift && allShiftsData && allShiftsData.length > 0) {
      const selectedShiftDetails = allShiftsData.find(s => s.id === propSelectedShift || s.name === propSelectedShift);
      if (selectedShiftDetails && selectedShiftDetails.pattern) {
        currentShiftPattern = selectedShiftDetails.pattern;
        if (selectedShiftDetails.patternStartDate) {
          patternStartDate = parseInputDate(selectedShiftDetails.patternStartDate);
          if (isNaN(patternStartDate.getTime())) {
            console.warn(`Invalid patternStartDate for shift ${propSelectedShift}: ${selectedShiftDetails.patternStartDate}. Defaulting pattern start date.`);
            patternStartDate = new Date(start.getFullYear(), 0, 1); // Default to start of year for pattern calculation
          }
        } else {
          console.warn(`patternStartDate not found for shift ${propSelectedShift}. Defaulting. This may lead to incorrect shift calculations.`);
          patternStartDate = new Date(start.getFullYear(), 0, 1);
        }
      }
    }

    let totalDays = 0;
    let workDaysCount = 0;
    let daysOffCount = 0;
    const newCalendarDays = [];
    const dayNamesForCalendar = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      totalDays++;
      const currentDate = new Date(d);
      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
      let isWorkDay = false;

      if (currentShiftPattern && patternStartDate && !isNaN(patternStartDate.getTime())) {
        // Use UTC to avoid DST issues and ensure accurate day counting
        const utcCurrent = Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        const utcStart = Date.UTC(patternStartDate.getFullYear(), patternStartDate.getMonth(), patternStartDate.getDate());
        const diffTime = utcCurrent - utcStart;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const patternIndex = (diffDays % currentShiftPattern.length + currentShiftPattern.length) % currentShiftPattern.length;
        const shiftCode = currentShiftPattern[patternIndex];
        if (shiftCode && shiftCode.toUpperCase() !== 'O' && shiftCode.toUpperCase() !== 'RDO') { // Assuming 'O' or 'RDO' means Off-day
          isWorkDay = true;
        }
      } else {
        // Fallback: Standard Mon-Fri work week
        if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Monday to Friday
          isWorkDay = true;
        }
      }

      if (isWorkDay) {
        workDaysCount++;
      } else {
        daysOffCount++;
      }

      newCalendarDays.push({
        dateStr: formatDateForInput(currentDate),
        dayOfMonth: currentDate.getDate(),
        dayName: dayNamesForCalendar[dayOfWeek],
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
        isWorkDay: isWorkDay,
      });
    }

    setSummary({
      totalDays: totalDays,
      workDays: workDaysCount,
      daysOff: daysOffCount,
      holidaysNeeded: workDaysCount 
    });
    setCalendarDays(newCalendarDays);

  }, [startDateStr, endDateStr, propSelectedShift, allShiftsData, parseInputDate, formatDateForInput, setSummary, setCalendarDays]);

  // Update holiday details when relevant props change
  useEffect(() => {
    // This effect now correctly recalculates based on all relevant data changes.
    updateHolidayDetails();
  }, [startDateStr, endDateStr, propSelectedShift, allShiftsData, updateHolidayDetails]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = parseInputDate(dateStr);
    return `${date.getDate().toString().padStart(2, '0')} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  // --- UI FLOW --- (Corrected and Simplified)
  let currentStageUI = null;

// Stage 0: Initial choice - What would you like to do?
if (plannerStage === 0) {
  currentStageUI = (
    <Container className="holiday-planner-container mt-3 mb-3">
      <div className="p-3">
        <div className="mb-3">
          <h3 className="h5 mb-0">Holiday Options</h3>
        </div>
        <div className="text-center">
          <p>What would you like to do?</p>
          <Button 
            variant="primary" 
            className="me-2 mb-2" 
            onClick={() => setPlannerStage(1)} // Go to Stage 1: Select Start Date
          >
            Plan New Holiday
          </Button>
          <Button 
            variant="info" 
            className="mb-2" 
            onClick={() => setPlannerStage(4)} // Go to Stage 4: View My Booked Holidays
          >
            View My Booked Holidays
          </Button>
          <Button variant="outline-danger" onClick={onClose} className="mt-md-0 mt-2 ms-md-2">
            Close Planner
          </Button>
        </div>
      </div>
    </Container>
  );
}

// Stage 1: Select start date
if (plannerStage === 1) {
  currentStageUI = (
    <Container className="holiday-planner-container mt-3 mb-3">
      <div className="p-3">
        <div className="mb-3">
          <h3 className="h5 mb-0">Select Holiday Start Date</h3>
        </div>
        <div>
          <SimpleDatePicker
            key="startDatePicker" // Added key prop
            selectedDate={startDateStr} // Prop for SimpleDatePicker
            onDateChange={setStartDateStr} // Prop for SimpleDatePicker
            label="Start Date"
            minDate={formatDateForInput(new Date())} // Prevent selecting dates before today
            // maxDate={endDateStr} // Removed: Allow any future start date initially
          />
          <div className="d-flex justify-content-between mt-4">
            <Button variant="secondary" onClick={() => setPlannerStage(0)}>Back</Button>
            <Button variant="primary" onClick={() => {
              setEndDateStr(startDateStr); // Set end date to start date before moving to next stage
              setPlannerStage(2);
            }}>Next</Button>
          </div>
        </div>
      </div>
    </Container>
  );
}

// Stage 2: Select end date
if (plannerStage === 2) {
  currentStageUI = (
    <Container className="holiday-planner-container mt-3 mb-3">
      <div className="p-3">
        <div className="mb-3">
          <h3 className="h5 mb-0">Select Holiday End Date</h3>
        </div>
        <div>
          <SimpleDatePicker
            key="endDatePicker" // Added key prop
            selectedDate={endDateStr} // Prop for SimpleDatePicker
            onDateChange={setEndDateStr} // Prop for SimpleDatePicker
            minDate={startDateStr}      // Prevent end date being before start date
            label="End Date"
          />
          <div className="d-flex justify-content-between mt-4">
            <Button variant="secondary" onClick={() => setPlannerStage(1)}>Back</Button>
            <Button variant="primary" onClick={() => setPlannerStage(3)}>Next</Button>
          </div>
        </div>
      </div>
    </Container>
  );
}

// Stage 3: Summary and confirmation
if (plannerStage === 3) {
  currentStageUI = (
    <Container className="holiday-planner-container mt-3 mb-3">
      <div className="p-3">
        <div className="mb-3">
          <h3 className="h5 mb-0">Holiday Summary</h3>
        </div>
        <div>
          <div className="mb-2">Start Date: <strong>{formatDate(startDateStr)}</strong></div>
          <div className="mb-2">End Date: <strong>{formatDate(endDateStr)}</strong></div>
          <hr />
          <div className="mb-2">Total Days Selected: <strong>{summary.totalDays}</strong></div>
          <div className="mb-2">Work Days (Mon-Fri): <strong>{summary.workDays}</strong></div>
          <div className="mb-2">Weekend Days (Sat-Sun): <strong>{summary.daysOff}</strong></div>
          <div className="mb-2"><strong>Holidays to Book: {summary.holidaysNeeded}</strong></div>
          
          {/* Optional: Display a simple calendar view for the selected range */}
          {calendarDays.length > 0 && (
            <div className="mt-4">
              <h6 className="mb-2">Selected Period Overview:</h6>
              <div className="calendar-header mb-1">
                {dayNames.map((dayName, index) => (
                  <div key={index} className="calendar-day-header small">
                    {dayName}
                  </div>
                ))}
              </div>
              <div className="holiday-calendar summary-calendar">
                {calendarDays.map((day, index) => (
                  <div 
                    key={index} 
                    className={`calendar-day small ${day.isWeekend ? 'weekend' : 'workday'}`}
                  >
                    {day.day} {/* Displaying only the day number */}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="d-flex justify-content-between mt-4">
            <Button variant="secondary" onClick={() => setPlannerStage(2)}>Back</Button>
            <Button 
              variant="success" 
              onClick={handleConfirmHoliday} 
              disabled={summary.holidaysNeeded <= 0} // Disable if no holidays to book
            >
              Confirm Holiday
            </Button>
          </div>
        </div>
      </div>
    </Container>
  );
}

// Stage 4: View My Booked Holidays (This was the original 'View Booked Holidays' UI)
if (plannerStage === 4) {
  currentStageUI = (
    <Container className="holiday-planner-container mt-3 mb-3">
      <div className="p-3">
        <div className="mb-3">
          <h3 className="h5 mb-0">Your Booked Holidays</h3>
        </div>
        <div>
          {loadingBookedHolidays && <p>Loading your holidays...</p>}
          {errorBookedHolidays && <Alert variant="danger">{errorBookedHolidays}</Alert>}
          {!loadingBookedHolidays && !errorBookedHolidays && userBookedHolidays.length === 0 && (
            <Alert variant="info">You have no holidays booked.</Alert>
          )}
          {!loadingBookedHolidays && !errorBookedHolidays && userBookedHolidays.length > 0 && (
            <ListGroup>
              {userBookedHolidays.map(holiday => (
                <ListGroup.Item key={holiday.id} className="d-flex justify-content-between align-items-center flex-wrap">
                  <div className="me-3">
                    <span>From: <strong>{formatDate(formatDateForInput(holiday.startDate))}</strong></span>
                    <br />
                    <span>To: <strong>{formatDate(formatDateForInput(holiday.endDate))}</strong></span>
                  </div>
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={() => handleCancelHoliday(holiday.id)} 
                    className="mt-2 mt-md-0"
                  >
                    Cancel Holiday
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
          <Button variant="secondary" className="mt-4" onClick={() => setPlannerStage(0)}>Back to Options</Button>
        </div>
      </div>
    </Container>
  );
}


  return (
    <>
      {currentStageUI}
      <NotificationModal 
        show={showNotificationModal}
        handleClose={() => setShowNotificationModal(false)}
        title={notificationConfig.title}
        message={notificationConfig.message}
        variant={notificationConfig.variant}
      />
      <ConfirmationModal 
        show={showConfirmationModal}
        handleClose={() => {
          setShowConfirmationModal(false);
        }}
        handleConfirm={confirmationConfig.onConfirm} // This now directly calls executeHolidayCancellation with the correct ID
        title={confirmationConfig.title}
        message={confirmationConfig.message}
      />
    </>
  ); // Render current stage UI and both modals
};

export default HolidayPlanner;
